import React, { useState, useMemo } from 'react';
import './TradeJournal.css';

const fmt = (n, d = 2) => n != null && isFinite(n) ? parseFloat(n).toFixed(d) : '—';
const fmtP = (n) => {
    if (n == null || !isFinite(n)) return '—';
    const v = parseFloat(n);
    return (v >= 0 ? '+' : '') + '$' + Math.abs(v).toFixed(2);
};
const fmtPct = (n) => {
    if (n == null || !isFinite(n)) return '—';
    const v = parseFloat(n);
    return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
};
const dur = (start, end) => {
    const ms = new Date(end || Date.now()) - new Date(start);
    if (ms < 0) return '—';
    const m = Math.floor(ms / 60000);
    return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const TradeJournal = ({ trades = [] }) => {
    const [filter, setFilter] = useState('ALL'); // ALL | WIN | LOSS | SIM | LIVE
    const [sortBy, setSortBy] = useState('date_desc');
    const [search, setSearch] = useState('');
    const [alertStatus, setAlertStatus] = useState(null);

    const closed = useMemo(() => trades.filter(t => t.status === 'CLOSED'), [trades]);

    const filtered = useMemo(() => {
        let list = [...closed];
        if (filter === 'WIN')  list = list.filter(t => parseFloat(t.profitLoss || 0) >= 0);
        if (filter === 'LOSS') list = list.filter(t => parseFloat(t.profitLoss || 0) < 0);
        if (filter === 'SIM')  list = list.filter(t => t.isSimulation);
        if (filter === 'LIVE') list = list.filter(t => !t.isSimulation);
        if (search) list = list.filter(t => t.pair?.toLowerCase().includes(search.toLowerCase()));
        if (sortBy === 'date_desc') list.sort((a, b) => new Date(b.closedAt || b.timestamp) - new Date(a.closedAt || a.timestamp));
        if (sortBy === 'date_asc')  list.sort((a, b) => new Date(a.closedAt || a.timestamp) - new Date(b.closedAt || b.timestamp));
        if (sortBy === 'pnl_desc')  list.sort((a, b) => parseFloat(b.profitLoss || 0) - parseFloat(a.profitLoss || 0));
        if (sortBy === 'pnl_asc')   list.sort((a, b) => parseFloat(a.profitLoss || 0) - parseFloat(b.profitLoss || 0));
        return list;
    }, [closed, filter, sortBy, search]);

    const stats = useMemo(() => {
        const live = closed.filter(t => !t.isSimulation);
        const wins = live.filter(t => parseFloat(t.profitLoss || 0) >= 0);
        const total = live.reduce((s, t) => s + parseFloat(t.profitLoss || 0), 0);
        return {
            total: live.length,
            wins: wins.length,
            losses: live.length - wins.length,
            winRate: live.length > 0 ? (wins.length / live.length * 100).toFixed(1) : '—',
            totalPnl: total,
            avgPnl: live.length > 0 ? total / live.length : 0,
        };
    }, [closed]);

    const testTelegram = async () => {
        setAlertStatus('sending…');
        try {
            const r = await fetch('/api/alerts/test', { method: 'POST' });
            const d = await r.json();
            setAlertStatus(d.success ? '✅ Sent! Check Telegram.' : '❌ ' + d.error);
        } catch (e) {
            setAlertStatus('❌ ' + e.message);
        }
    };

    return (
        <div className="trade-journal">
            <div className="tj-header">
                <h2>📒 Trade Journal</h2>
                <div className="tj-subtitle">Full history of closed trades — real and simulated</div>
            </div>

            {/* Stats bar */}
            <div className="tj-stats">
                <div className="tj-stat"><span className="tj-stat-label">Closed (live)</span><span className="tj-stat-val">{stats.total}</span></div>
                <div className="tj-stat"><span className="tj-stat-label">Win rate</span><span className="tj-stat-val tj-green">{stats.winRate}{stats.winRate !== '—' ? '%' : ''}</span></div>
                <div className="tj-stat"><span className="tj-stat-label">Total P&L</span><span className={`tj-stat-val ${stats.totalPnl >= 0 ? 'tj-green' : 'tj-red'}`}>{fmtP(stats.totalPnl)}</span></div>
                <div className="tj-stat"><span className="tj-stat-label">Avg P&L</span><span className={`tj-stat-val ${stats.avgPnl >= 0 ? 'tj-green' : 'tj-red'}`}>{fmtP(stats.avgPnl)}</span></div>
                <div className="tj-stat"><span className="tj-stat-label">Wins / Losses</span><span className="tj-stat-val"><span className="tj-green">{stats.wins}</span> / <span className="tj-red">{stats.losses}</span></span></div>
            </div>

            {/* Telegram alert test */}
            <div className="tj-alert-row">
                <span className="tj-alert-label">📨 Telegram alerts</span>
                <button className="tj-alert-btn" onClick={testTelegram}>Send test message</button>
                {alertStatus && <span className="tj-alert-status">{alertStatus}</span>}
                {!alertStatus && <span className="tj-alert-hint">Set TELEGRAM_BOT_TOKEN + TELEGRAM_CHAT_ID in .env to receive STRONG BUY/SELL alerts</span>}
            </div>

            {/* Filters */}
            <div className="tj-controls">
                <input className="tj-search" placeholder="🔍 Search pair…" value={search} onChange={e => setSearch(e.target.value)} />
                <div className="tj-filters">
                    {['ALL','WIN','LOSS','LIVE','SIM'].map(f => (
                        <button key={f} className={`tj-filter-btn${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
                    ))}
                </div>
                <select className="tj-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    <option value="date_desc">Newest first</option>
                    <option value="date_asc">Oldest first</option>
                    <option value="pnl_desc">Best P&L first</option>
                    <option value="pnl_asc">Worst P&L first</option>
                </select>
            </div>

            {/* Table */}
            {filtered.length === 0 ? (
                <div className="tj-empty">No closed trades yet. Trades will appear here after you close them.</div>
            ) : (
                <div className="tj-table-wrap">
                    <table className="tj-table">
                        <thead><tr>
                            <th>Pair</th><th>Dir</th><th>Entry</th><th>Exit</th>
                            <th>P&L</th><th>%</th><th>Lev</th><th>Amt</th>
                            <th>Signal</th><th>Duration</th><th>Closed</th>
                        </tr></thead>
                        <tbody>
                            {filtered.map(t => {
                                const pnl = parseFloat(t.profitLoss || 0);
                                const win = pnl >= 0;
                                return (
                                    <tr key={t.id} className={win ? 'tj-win' : 'tj-loss'}>
                                        <td><b>{t.pair}</b>{t.isSimulation && <span className="tj-sim-tag">SIM</span>}</td>
                                        <td><span className={`tj-dir ${t.action === 'BUY' ? 'tj-buy' : 'tj-sell'}`}>{t.action}</span></td>
                                        <td>${fmt(t.entryPrice, 4)}</td>
                                        <td>${fmt(t.exitPrice, 4)}</td>
                                        <td className={win ? 'tj-green' : 'tj-red'}>{fmtP(pnl)}</td>
                                        <td className={win ? 'tj-green' : 'tj-red'}>{fmtPct(t.profitPercent)}</td>
                                        <td>{t.leverage}x</td>
                                        <td>${t.amount}</td>
                                        <td><span className="tj-signal">{t.signal || '—'}</span></td>
                                        <td>{dur(t.timestamp, t.closedAt)}</td>
                                        <td className="tj-date">{t.closedAt ? new Date(t.closedAt).toLocaleString() : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TradeJournal;
